import React from 'react';
import {useFormContext, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router';

import {SectionWithBorderField} from '@edna/components';
import {Icon, Stack} from '@edna/components/primitives';

import {TChannelType, channelIconHash} from 'src/constants';

import {SellingTariffContent} from '../TariffCard';
import {useGetAvailableActiveTariffsQuery} from '../api';
import * as S from './style';

type TProps = {
  channelType: TChannelType;
  name: string;
  readOnly?: boolean;
  disabled?: boolean;
};

export const SellingTariffSelectField = React.memo<TProps>(
  ({channelType, name, disabled, readOnly}) => {
    const {t} = useTranslation();
    const [searchParams] = useSearchParams();
    const tariffId = searchParams.get('tariffId');

    const {data: tariffs = []} = useGetAvailableActiveTariffsQuery(channelType);

    const {setValue, control} = useFormContext();
    const value = useWatch({name, control});

    const hasInteraction = !readOnly && !disabled;

    React.useEffect(() => {
      if (value || !hasInteraction) {
        return;
      }

      if (tariffId) {
        setValue(name, Number(tariffId), {
          shouldDirty: true,
          shouldValidate: true,
          shouldTouch: true,
        });
      }

      if (tariffs.length === 1) {
        setValue(name, tariffs[0].id, {shouldDirty: true});
      }
    }, [tariffId, tariffs.length]);

    return (
      <SectionWithBorderField
        name={name}
        readOnly={!hasInteraction}
        label={t('Tariffs:selectTariff')}
      >
        <S.TariffsList count={tariffs.length}>
          {tariffs.map((tariff) => (
            <S.TariffWrapper
              isActive={hasInteraction && value === tariff.id}
              hasPointer={hasInteraction}
              key={tariff.id}
              onClick={() => setValue(name, tariff.id, {shouldDirty: true, shouldValidate: true})}
            >
              <Stack direction="ROW" size="5">
                {tariffs.length > 1 && (
                  <S.RadioField
                    name={name}
                    type="radio"
                    value={tariff.id}
                    readOnly={readOnly}
                    disabled={disabled}
                    width="auto"
                  />
                )}
                <S.TariffTitle>
                  <Icon as={channelIconHash[tariff.channelType]} size="32px" />
                  {tariff.name}
                </S.TariffTitle>
              </Stack>
              <SellingTariffContent tariff={tariff} />
            </S.TariffWrapper>
          ))}
        </S.TariffsList>
      </SectionWithBorderField>
    );
  },
);

SellingTariffSelectField.displayName = 'SellingTariffSelectField';
